import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import type { AiSpendBudgetView, AiSpendFailureView, AiSpendGroupView, AiSpendReportView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { AUTO_REFRESH_MS, Badge, Button, Cluster, EmptyState, Field, FormRow, Grid, Meter, Notice, PageHeader, Panel, Select, Stack, StatCard, Table, Text, TextInput, useAutoRefresh } from '@vello/web-theme';
import {
  screenAllowed,
  aiSpendAuthority,
  budgetRows,
  budgetTone,
  isRateText,
  microsMoney,
  minorMoney,
  missingRatePairs,
  monthChoices,
  percentOfBp,
  perCallMicros,
  providerAccountIssue,
  shareBp,
  tailOf,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { usePersonNames } from '../components/person-name';

/**
 * **Configurações › Gastos de IA** (D639, Digital Central phase 4, doc 25 section 11), over `AiSpendApi` (`/v1/ai/spend`, `/v1/ai/fx-rates`).
 *
 * What the Central's AI cost in a UTC month - by purpose, model, country and channel - the cost of each conversation Vello resolved on its own, the
 * end-of-month forecast at the current pace, and every budget (`ai.monthlyBudget`: the platform's and each country's) against its alerts. At 100 percent
 * the assistant stops spending and hands conversations to people: the screen says so, it is never a silent overspend. OpenRouter bills in one currency
 * and a budget may be in another: the comparison needs an exchange rate an operator recorded (append-only); without it the budget reads "cannot be
 * compared" and the form to record the rate is offered (`ai_spend:manage`). A refused or failed read is an error, never zero spend. Every state and
 * purpose is read through the central vocabulary; money is formatted by the console, the server only sends exact integers.
 */

type Msg = { readonly tone: 'success' | 'danger' | 'warning'; readonly text: string } | undefined;

export function AiSpendScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const countryName = useCountryName();
  const { mayView, mayManage } = aiSpendAuthority(grants);
  const [month, setMonth] = useState('');
  const [report, setReport] = useState<AiSpendReportView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);
  const [fx, setFx] = useState({ from: 'USD', to: '', rate: '' });
  const personName = usePersonNames((report?.rates ?? []).map((r) => r.setBy));
  const months = useMemo(() => monthChoices(new Date().toISOString()), []);

  const load = useCallback(async (): Promise<void> => {
    if (!mayView) return;
    const res = await runtime.aiSpend.spend(month === '' ? undefined : month);
    if (res.ok) {
      setReport(res.value);
      setError(undefined);
    } else {
      // A refused read keeps the spend unknown: the screen says it could not load, never "no spend".
      setReport(undefined);
      setError(fail(res.error));
    }
  }, [runtime, mayView, month, fail]);

  useEffect(() => {
    setReport(undefined);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, mayView]);
  useAutoRefresh(() => load(), { intervalMs: AUTO_REFRESH_MS.queue, enabled: mayView && !busy });

  const money = (micros: string, currency: string): string => microsMoney(micros, currency, lang);
  const minor = (amount: string | undefined, currency: string): string => (amount === undefined ? '—' : minorMoney(amount, currency, lang));
  const scopeName = (b: AiSpendBudgetView): string => (b.scope === 'platform' ? t('aispend.scope.platform') : countryName(b.country));

  const rateIssue = ((): string | undefined => {
    if (!isIso4217Currency(fx.from) || !isIso4217Currency(fx.to) || fx.from === fx.to) return t('aispend.fx.invalidPair');
    if (!isRateText(fx.rate)) return t('aispend.fx.invalidRate');
    return undefined;
  })();

  const saveRate = async (): Promise<void> => {
    if (rateIssue !== undefined || busy) return;
    setBusy(true);
    setMsg(undefined);
    const res = await runtime.aiSpend.putFxRate({ from: fx.from, to: fx.to, rate: fx.rate.trim() });
    setBusy(false);
    if (res.ok) {
      setMsg({ tone: 'success', text: t('aispend.fx.saved', { from: fx.from, to: fx.to }) });
      setFx((f) => ({ ...f, rate: '' }));
      await load();
    } else {
      setMsg({ tone: 'danger', text: fail(res.error) });
    }
  };

  const groupTable = (title: string, hint: string, groups: readonly AiSpendGroupView[], label: (key: string | null) => string): ReactElement => (
    <Panel padded={false}>
      <Stack gap={3}>
        <h3>{title}</h3>
        <Text as="p" role="caption" tone="secondary">{hint}</Text>
        <Table
          columns={[
            { key: 'key', header: title, render: (g: AiSpendGroupView) => <span>{label(g.key)}</span> },
            { key: 'cost', header: t('aispend.col.cost'), numeric: true, render: (g: AiSpendGroupView) => <span className="tnum">{money(g.costMicros, g.currency)}</span> },
            { key: 'share', header: t('aispend.col.share'), numeric: true, render: (g: AiSpendGroupView) => <span className="tnum">{percentOfBp(shareBp(g, groups))}</span> },
            { key: 'calls', header: t('aispend.col.calls'), numeric: true, render: (g: AiSpendGroupView) => <span className="tnum">{g.calls.toLocaleString(lang)}</span> },
            { key: 'per', header: t('aispend.col.perCall'), numeric: true, render: (g: AiSpendGroupView) => <span className="tnum">{money(perCallMicros(g), g.currency)}</span> },
            { key: 'errors', header: t('aispend.col.errors'), numeric: true, render: (g: AiSpendGroupView) => <span className="tnum">{g.errorCalls.toLocaleString(lang)}</span> },
            { key: 'tokens', header: t('aispend.col.tokens'), numeric: true, render: (g: AiSpendGroupView) => <span className="tnum">{(g.inputTokens + g.outputTokens).toLocaleString(lang)}</span> },
          ]}
          rows={groups}
          rowKey={(g) => `${g.key ?? ''}|${g.currency}`}
          empty={<EmptyState title={t('aispend.empty')} />}
        />
      </Stack>
    </Panel>
  );

  if (!mayView) {
    return (
      <>
        <PageHeader title={t('aispend.title')} subtitle={t('aispend.subtitle')} />
        <Panel><EmptyState title={t('aispend.noPermission')} /></Panel>
      </>
    );
  }

  const pairs = report === undefined ? [] : missingRatePairs(report.budgets);
  const accountIssue = providerAccountIssue(report?.failures);
  const none = t('aispend.none');

  return (
    <>
      <PageHeader
        title={t('aispend.title')}
        subtitle={t('aispend.subtitle')}
        actions={(
          <Field label={t('aispend.month')} htmlFor="aispend-month">
            <Select id="aispend-month" value={month === '' ? (months[0] ?? '') : month} onChange={(ev) => setMonth(ev.target.value)}>
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </Select>
          </Field>
        )}
      />
      <Panel><Text as="p" role="caption" tone="secondary">{t('aispend.explain')}</Text></Panel>
      {error !== undefined && <Panel><Notice tone="danger">{t('aispend.loadFailed', { reason: error })}</Notice></Panel>}
      {msg !== undefined && <Panel><Notice tone={msg.tone}>{msg.text}</Notice></Panel>}
      {report === undefined && error === undefined && <Panel><EmptyState title={t('ui.loading')} /></Panel>}

      {report !== undefined && (
        <>
          {/* ---- the totals and the forecast ---- */}
          <Panel>
            <Stack gap={3}>
              <h3>{t('aispend.totals.title')}</h3>
              <Text as="p" role="caption" tone="secondary">{t('aispend.totals.days', { elapsed: report.daysElapsed, total: report.daysInMonth })}</Text>
              {report.totals.length === 0 ? <EmptyState title={t('aispend.empty')} /> : (
                <Grid min="sm" gap={3}>
                  {report.totals.map((total) => {
                    const forecast = report.forecast.find((f) => f.currency === total.currency);
                    return (
                      <Stack key={total.currency} gap={3}>
                        <StatCard label={t('aispend.stat.spent', { currency: total.currency })} value={money(total.costMicros, total.currency)} hint={t('aispend.stat.calls', { calls: total.calls.toLocaleString(lang), errors: total.errorCalls.toLocaleString(lang) })} />
                        <StatCard label={t('aispend.stat.forecast', { currency: total.currency })} value={forecast === undefined ? '—' : money(forecast.projectedMicros, total.currency)} hint={t('aispend.stat.forecast.hint')} />
                      </Stack>
                    );
                  })}
                </Grid>
              )}
            </Stack>
          </Panel>

          {/* ---- the budgets ---- */}
          <Panel>
            <Stack gap={3}>
              <h3>{t('aispend.budgets.title')}</h3>
              <Text as="p" role="caption" tone="secondary">{t('aispend.budgets.hint')}</Text>
              {report.budgets.length === 0 ? <EmptyState title={t('aispend.budgets.none')} /> : budgetRows(report.budgets).map((b) => (
                <Stack key={`${b.scope}|${b.country ?? ''}`} gap={2}>
                  <Cluster gap={3} align="baseline" wrap>
                    <Text as="h4" role="label">{scopeName(b)}</Text>
                    <Badge tone={budgetTone(b.state)}>{v('ai.spend_budget_state', b.state)}</Badge>
                    {/* D768: the budget is the governed rule `ai.monthlyBudget` — it changes in Regras governadas, opened on that rule at this place. */}
                    {screenAllowed(grants, 'governed_settings') && (b.scope === 'platform' || b.country !== undefined) && (
                      <Button size="sm" variant="ghost" icon="pencil" onClick={() => navigate('governed_settings', {}, { key: 'ai.monthlyBudget', level: b.scope === 'platform' ? 'platform|' : `country|${b.country ?? ''}` })}>
                        {t(b.state === 'UNSET' ? 'aispend.budgets.set' : 'aispend.budgets.edit')}
                      </Button>
                    )}
                  </Cluster>
                  {b.budget !== undefined && b.usedBasisPoints !== undefined && (
                    <>
                      <Meter label={t('aispend.budgets.used')} value={b.usedBasisPoints} max={10_000} showMax={false} warnAt={0.8} dangerAt={1} formatValue={(x) => percentOfBp(x)} />
                      <Text role="caption" tone="secondary"><span className="tnum">{t('aispend.budgets.line', { spent: minor(b.spentMinor, b.budget.currency), budget: minor(b.budget.amountMinor, b.budget.currency) })}</span></Text>
                      <Text role="caption" tone="secondary"><span className="tnum">{t('aispend.budgets.projected', { projected: minor(b.projectedMinor, b.budget.currency), percent: percentOfBp(b.projectedBasisPoints) })}</span></Text>
                      <Text role="caption" tone="tertiary">{t('aispend.budgets.alerts', { percents: b.budget.alertPercents.map((p) => `${String(p)}%`).join(', ') })}</Text>
                      {b.willExceed === true && b.state !== 'EXCEEDED' && <Notice tone="warning">{t('aispend.budgets.willExceed')}</Notice>}
                    </>
                  )}
                  {b.state === 'EXCEEDED' && <Notice tone="danger">{t('aispend.budgets.exceeded')}</Notice>}
                  {b.state === 'UNSET' && <Text role="caption" tone="secondary">{t('aispend.budgets.unset')}</Text>}
                  {b.state === 'INVALID' && <Notice tone="danger">{t('aispend.budgets.invalid')}</Notice>}
                  {(b.missingRates ?? []).map((pair) => <Notice key={pair} tone="warning">{t('aispend.budgets.missingRate', { pair: pair.replace('->', ' → ') })}</Notice>)}
                  {(b.unknownCostCalls ?? 0) > 0 && <Notice tone="warning">{t('aispend.budgets.unknownCost', { calls: (b.unknownCostCalls ?? 0).toLocaleString(lang) })}</Notice>}
                </Stack>
              ))}
            </Stack>
          </Panel>

          {/* ---- the thresholds crossed this month ---- */}
          {report.alerts.length > 0 && (
            <Panel>
              <Stack gap={2}>
                <h3>{t('aispend.alerts.title')}</h3>
                <Text as="p" role="caption" tone="secondary">{t('aispend.alerts.hint')}</Text>
                {report.alerts.map((a) => (
                  <Cluster key={`${a.scope}|${a.country ?? ''}|${String(a.thresholdPercent)}`} gap={3} align="baseline" wrap>
                    <Badge tone={a.thresholdPercent >= 100 ? 'danger' : 'warning'}>{t('aispend.alerts.crossed', { percent: String(a.thresholdPercent) })}</Badge>
                    <Text>{a.scope === 'platform' ? t('aispend.scope.platform') : countryName(a.country ?? undefined)}</Text>
                    <Text role="caption" tone="secondary"><span className="tnum">{t('aispend.budgets.line', { spent: minor(a.spentMinor, a.currency), budget: minor(a.budgetMinor, a.currency) })}</span></Text>
                    <Text role="caption" tone="tertiary"><span className="tnum">{new Date(a.raisedAt).toLocaleString(lang, { timeZone: 'UTC' })}</span></Text>
                  </Cluster>
                ))}
              </Stack>
            </Panel>
          )}

          {/* ---- D851: why the calls failed (by purpose and reason, with the provider's own last words) ---- */}
          {(report.failures ?? []).length > 0 && (
            <Panel padded={false}>
              <Stack gap={3}>
                <h3>{t('aispend.failures.title')}</h3>
                <Text as="p" role="caption" tone="secondary">{t('aispend.failures.hint')}</Text>
                {accountIssue !== undefined && (
                  <Notice tone="danger">{t(accountIssue.kind === 'key' ? 'aispend.failures.key' : 'aispend.failures.credits', { at: new Date(accountIssue.lastAt).toLocaleString(lang, { timeZone: 'UTC' }) })}</Notice>
                )}
                <Table
                  columns={[
                    { key: 'reason', header: t('aispend.failures.col.reason'), render: (f: AiSpendFailureView) => <span>{v('ai.call_failure', f.reasonCode)}</span> },
                    { key: 'purpose', header: t('aispend.group.purpose'), render: (f: AiSpendFailureView) => <span>{v('ai.purpose', f.purpose)}</span> },
                    { key: 'calls', header: t('aispend.col.calls'), numeric: true, render: (f: AiSpendFailureView) => <span className="tnum">{f.calls.toLocaleString(lang)}</span> },
                    { key: 'last', header: t('aispend.failures.col.last'), render: (f: AiSpendFailureView) => <span className="tnum">{new Date(f.lastAt).toLocaleString(lang, { timeZone: 'UTC' })}</span> },
                    { key: 'model', header: t('aispend.group.model'), render: (f: AiSpendFailureView) => <span>{f.lastModel}</span> },
                    { key: 'detail', header: t('aispend.failures.col.detail'), render: (f: AiSpendFailureView) => <Text role="caption" tone="secondary">{f.lastDetail ?? '—'}</Text> },
                  ]}
                  rows={report.failures ?? []}
                  rowKey={(f) => `${f.purpose}|${f.reasonCode}`}
                  empty={<EmptyState title={t('aispend.empty')} />}
                />
              </Stack>
            </Panel>
          )}

          {/* ---- the cost of a conversation Vello resolved ---- */}
          <Panel padded={false}>
            <Stack gap={3}>
              <h3>{t('aispend.resolved.title')}</h3>
              <Text as="p" role="caption" tone="secondary">{t('aispend.resolved.hint')}</Text>
              <Table
                columns={[
                  { key: 'country', header: t('aispend.group.country'), render: (r: AiSpendReportView['costPerResolved'][number]) => <span>{r.country === null ? none : countryName(r.country)}</span> },
                  { key: 'conversations', header: t('aispend.resolved.col.conversations'), numeric: true, render: (r: AiSpendReportView['costPerResolved'][number]) => <span className="tnum">{r.conversations.toLocaleString(lang)}</span> },
                  { key: 'cost', header: t('aispend.col.cost'), numeric: true, render: (r: AiSpendReportView['costPerResolved'][number]) => <span className="tnum">{money(r.costMicros, r.currency)}</span> },
                  { key: 'per', header: t('aispend.resolved.col.per'), numeric: true, render: (r: AiSpendReportView['costPerResolved'][number]) => <span className="tnum">{money(r.perConversationMicros, r.currency)}</span> },
                ]}
                rows={report.costPerResolved}
                rowKey={(r) => `${r.country ?? ''}|${r.currency}`}
                empty={<EmptyState title={t('aispend.resolved.empty')} />}
              />
            </Stack>
          </Panel>

          {groupTable(t('aispend.group.purpose'), t('aispend.group.purpose.hint'), report.byPurpose, (k) => (k === null ? none : v('ai.purpose', k)))}
          {groupTable(t('aispend.group.model'), t('aispend.group.model.hint'), report.byModel, (k) => k ?? none)}
          {groupTable(t('aispend.group.country'), t('aispend.group.country.hint'), report.byCountry, (k) => (k === null ? none : countryName(k)))}
          {groupTable(t('aispend.group.channel'), t('aispend.group.channel.hint'), report.byChannel, (k) => (k === null ? none : v('conversation.channel', k)))}

          {/* ---- the days ---- */}
          <Panel padded={false}>
            <Stack gap={3}>
              <h3>{t('aispend.daily.title')}</h3>
              <Table
                columns={[
                  { key: 'date', header: t('aispend.daily.col.date'), render: (d: AiSpendReportView['daily'][number]) => <span className="tnum">{d.date}</span> },
                  { key: 'calls', header: t('aispend.col.calls'), numeric: true, render: (d: AiSpendReportView['daily'][number]) => <span className="tnum">{d.calls.toLocaleString(lang)}</span> },
                  { key: 'cost', header: t('aispend.col.cost'), numeric: true, render: (d: AiSpendReportView['daily'][number]) => <span className="tnum">{money(d.costMicros, d.currency)}</span> },
                ]}
                rows={report.daily}
                rowKey={(d) => `${d.date}|${d.currency}`}
                empty={<EmptyState title={t('aispend.empty')} />}
              />
            </Stack>
          </Panel>

          {/* ---- the exchange rates ---- */}
          <Panel>
            <Stack gap={3}>
              <h3>{t('aispend.fx.title')}</h3>
              <Text as="p" role="caption" tone="secondary">{t('aispend.fx.hint')}</Text>
              <Table
                columns={[
                  { key: 'pair', header: t('aispend.fx.col.pair'), render: (r: AiSpendReportView['rates'][number]) => <span className="tnum">{r.from} → {r.to}</span> },
                  { key: 'rate', header: t('aispend.fx.col.rate'), numeric: true, render: (r: AiSpendReportView['rates'][number]) => <span className="tnum">{r.rate ?? r.rateMicros}</span> },
                  { key: 'from', header: t('aispend.fx.col.effective'), render: (r: AiSpendReportView['rates'][number]) => <span className="tnum">{new Date(r.effectiveFrom).toLocaleDateString(lang, { timeZone: 'UTC' })}</span> },
                  { key: 'by', header: t('aispend.fx.col.by'), render: (r: AiSpendReportView['rates'][number]) => <span>{personName(r.setBy) ?? tailOf(r.setBy)}</span> },
                ]}
                rows={report.rates}
                rowKey={(r) => `${r.from}|${r.to}|${r.effectiveFrom}`}
                empty={<EmptyState title={t('aispend.fx.empty')} />}
              />
              {mayManage && (
                <Stack gap={3}>
                  <Text role="label">{t('aispend.fx.form.title')}</Text>
                  {pairs.length > 0 && (
                    <Cluster gap={2} wrap>
                      {pairs.map((p) => (
                        <Button key={`${p.from}|${p.to}`} size="sm" variant="secondary" onClick={() => setFx({ from: p.from, to: p.to, rate: '' })}>
                          {t('aispend.fx.suggest', { from: p.from, to: p.to })}
                        </Button>
                      ))}
                    </Cluster>
                  )}
                  <FormRow>
                    <Field label={t('aispend.fx.form.from')} htmlFor="aispend-fx-from">
                      <TextInput id="aispend-fx-from" value={fx.from} maxLength={3} onChange={(ev) => setFx((f) => ({ ...f, from: ev.target.value.toUpperCase() }))} />
                    </Field>
                    <Field label={t('aispend.fx.form.to')} htmlFor="aispend-fx-to">
                      <TextInput id="aispend-fx-to" value={fx.to} maxLength={3} onChange={(ev) => setFx((f) => ({ ...f, to: ev.target.value.toUpperCase() }))} />
                    </Field>
                    <Field label={t('aispend.fx.form.rate')} htmlFor="aispend-fx-rate" hint={t('aispend.fx.form.rate.hint', { from: fx.from === '' ? '…' : fx.from, to: fx.to === '' ? '…' : fx.to })}>
                      <TextInput id="aispend-fx-rate" inputMode="decimal" value={fx.rate} maxLength={16} onChange={(ev) => setFx((f) => ({ ...f, rate: ev.target.value.replace(',', '.') }))} />
                    </Field>
                  </FormRow>
                  {fx.rate !== '' && rateIssue !== undefined && <Notice tone="warning">{rateIssue}</Notice>}
                  <Cluster gap={3}>
                    <Button variant="primary" disabled={busy || rateIssue !== undefined} onClick={() => void saveRate()}>{t('aispend.fx.form.save')}</Button>
                  </Cluster>
                </Stack>
              )}
            </Stack>
          </Panel>
        </>
      )}
    </>
  );
}
